import { effect, Injectable, signal } from '@angular/core';
import { en, es } from '../i18n/locales';

type LanguageCode = 'es' | 'en';

const LANGUAGE_STORAGE_KEY = 'sunbrickerp.language';
const LOCALES: Record<LanguageCode, string> = {
  es: 'es-MX',
  en: 'en-US',
};
const translations = { es, en } as const;

type CurrencyFormatOptions = {
  currency?: string;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
};

@Injectable({
  providedIn: 'root',
})
export class Language {
  readonly #language = signal<LanguageCode>(this.#getInitialLanguage());
  readonly currentLanguage = this.#language.asReadonly();

  constructor() {
    effect(() => {
      const language = this.#language();

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
      }

      if (typeof document !== 'undefined') {
        document.documentElement.lang = language;
      }
    });
  }

  setLanguage(language: LanguageCode) {
    this.#language.set(language);
  }

  formatDate(
    value: string | number | Date | null | undefined,
    options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    },
  ) {
    if (!value) {
      return '—';
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    return new Intl.DateTimeFormat(LOCALES[this.#language()], options).format(date);
  }

  formatBusinessDate(
    value: string | null | undefined,
    options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    },
  ) {
    if (!value) {
      return '—';
    }

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) {
      return '—';
    }

    const [, year, month, day] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day), 12);

    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    return new Intl.DateTimeFormat(LOCALES[this.#language()], options).format(date);
  }

  formatCurrency(value: number | null | undefined, options: CurrencyFormatOptions = {}) {
    if (value == null || Number.isNaN(value)) {
      return '—';
    }

    const {
      currency = 'MXN',
      minimumFractionDigits = 2,
      maximumFractionDigits = 2,
    } = options;

    return new Intl.NumberFormat(LOCALES[this.#language()], {
      style: 'currency',
      currency,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(value);
  }

  t(path: string, params?: Record<string, string | number>) {
    const value = path
      .split('.')
      .reduce<unknown>((currentValue, segment) => {
        if (
          currentValue &&
          typeof currentValue === 'object' &&
          segment in currentValue
        ) {
          return (currentValue as Record<string, unknown>)[segment];
        }

        return path;
      }, translations[this.#language()]);

    if (typeof value !== 'string') {
      return path;
    }

    if (!params) {
      return value;
    }

    return Object.entries(params).reduce((message, [key, replacement]) => {
      return message.replaceAll(`{{${key}}}`, String(replacement));
    }, value);
  }

  #getInitialLanguage(): LanguageCode {
    if (typeof window === 'undefined') {
      return 'es';
    }

    const storedValue = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (storedValue === 'es' || storedValue === 'en') {
      return storedValue;
    }

    return navigator.language.startsWith('en') ? 'en' : 'es';
  }
}
